import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useActiveOrg } from '../lib/activeOrg';
import type { WysiwygSaveState } from '../components/primitives';
import { snapshotEditorViewport } from '../lib/editor/preserveEditorViewport';

export interface DocumentEditorLoadedDoc {
  id: string;
  title: string;
  body: string;
  last_event_at: string;
  org_id: string;
  type_id: string;
}

export type DocumentEditorLoadState =
  | { kind: 'loading' }
  | { kind: 'notfound' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; doc: DocumentEditorLoadedDoc };

export interface UseDocumentEditorStateOptions {
  documentId: string | undefined;
  onError?: (message: string) => void;
  /**
   * Returns a DOM node inside the editor shell (typically the body editor
   * wrapper). Used by `reloadFromServer` to snapshot + restore the editor's
   * scroll position and caret around the inline content swap, so a committed
   * agent write to the OPEN document reconciles WITHOUT moving the user's view.
   * Optional — when absent the reconcile still runs, it just doesn't preserve
   * the viewport.
   */
  getViewportAnchor?: () => HTMLElement | null;
}

export interface UseDocumentEditorStateResult {
  load: DocumentEditorLoadState;
  titleDraft: string;
  setTitleDraft: (next: string) => void;
  bodyDraft: string;
  setBodyDraft: (next: string) => void;
  saveState: WysiwygSaveState;
  dirty: boolean;
  titleDirty: boolean;
  bodyDirty: boolean;
  onSave: () => Promise<void>;
  resetDrafts: () => void;
  applyRestoredVersion: (snapshot: { title: string; body: string; lastEventAt: string }) => void;
  mutateLoadedDoc: (updater: (doc: DocumentEditorLoadedDoc) => DocumentEditorLoadedDoc) => void;
  reloadFromServer: () => Promise<void>;
  /**
   * Change the document's Type via set_document_type. Uses the editor's
   * optimistic-concurrency timestamp and refreshes it from the RPC's returned
   * `updated_at` so a following body save does not trip a stale-timestamp
   * conflict. Reconciles `type_id`/`last_event_at` locally without a reload.
   * Returns false on error (surfaced via onError).
   */
  setType: (typeId: string) => Promise<boolean>;
  historyTick: number;
  bumpHistoryTick: () => void;
}

export function useDocumentEditorState(
  options: UseDocumentEditorStateOptions
): UseDocumentEditorStateResult {
  const { documentId, onError, getViewportAnchor } = options;
  // Active org owned by the shell. The document load is scoped to it so a
  // deep-link to a document in another org (which RLS would allow for a member
  // of both) resolves to "not found" instead of rendering cross-org.
  const { activeOrgId } = useActiveOrg();
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const getViewportAnchorRef = useRef(getViewportAnchor);
  getViewportAnchorRef.current = getViewportAnchor;

  const [load, setLoad] = useState<DocumentEditorLoadState>({ kind: 'loading' });
  const [titleDraft, setTitleDraft] = useState('');
  const [bodyDraft, setBodyDraft] = useState('');
  const [saveState, setSaveState] = useState<WysiwygSaveState>('idle');
  const [historyTick, setHistoryTick] = useState(0);
  const expectedTsRef = useRef<string | null>(null);
  const baselineTitleRef = useRef('');
  const baselineBodyRef = useRef('');
  // In-flight-save guard. Set while a user-initiated `onSave` is mid-RPC so a
  // concurrent `reloadFromServer` does not stomp the baseline/timestamp refs
  // the save is actively mutating (which would double-resolve / corrupt the
  // save outcome). A ref — not state — so it is readable synchronously inside
  // both callbacks without stale-closure hazards.
  const saveInFlightRef = useRef(false);

  useEffect(() => {
    if (!documentId) return;
    // Active org not resolved yet — show loading; the effect re-runs once the
    // shell resolves it (and on org switch, so an out-of-org doc goes notfound).
    if (!activeOrgId) {
      setLoad({ kind: 'loading' });
      return;
    }
    let cancelled = false;
    setLoad({ kind: 'loading' });
    setSaveState('idle');
    void (async () => {
      const { data, error } = await supabase
        .from('documents')
        .select(
          'id, title, body, last_event_at, org_id, type_id'
        )
        .eq('id', documentId)
        .eq('org_id', activeOrgId)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        setLoad({ kind: 'error', message: error.message });
        return;
      }
      if (!data) {
        setLoad({ kind: 'notfound' });
        return;
      }
      const doc: DocumentEditorLoadedDoc = data as DocumentEditorLoadedDoc;
      expectedTsRef.current = doc.last_event_at;
      baselineTitleRef.current = doc.title ?? '';
      baselineBodyRef.current = doc.body ?? '';
      setTitleDraft(doc.title ?? '');
      setBodyDraft(doc.body ?? '');
      setLoad({ kind: 'ready', doc });
    })();
    return () => {
      cancelled = true;
    };
  }, [documentId, activeOrgId]);

  const titleDirty = load.kind === 'ready' && titleDraft !== baselineTitleRef.current;
  const bodyDirty = load.kind === 'ready' && bodyDraft !== baselineBodyRef.current;
  const dirty = titleDirty || bodyDirty;

  const onSave = useCallback(async () => {
    if (load.kind !== 'ready' || !documentId) return;
    if (!dirty) return;
    setSaveState('saving');

    // Working timestamp for this save; kept in sync with expectedTsRef.current,
    // the durable source of truth, after every successful RPC and retry.
    let currentExpected = expectedTsRef.current!;

    /**
     * Verify-then-decide on a conflict error from a save RPC. The original
     * guarded RPC matched zero rows (its `p_expected_updated_at` is stale),
     * which raises a `P0001` conflict. But a stale timestamp does NOT mean a
     * content conflict — an orthogonal write (a concurrent rename when we're
     * saving the body, or a concurrent body edit when we're renaming) also
     * bumps `last_event_at`.
     *
     * So we re-read the live row and compare ONLY the field this RPC writes
     * against its baseline:
     *  - the deciding field diverged from baseline → genuine conflict.
     *  - the deciding field still matches → only an orthogonal column moved
     *    the timestamp; refresh `currentExpected` and retry the RPC once.
     *
     * The verify-read fetches both columns so that after a successful retry
     * we can opportunistically refresh the OTHER field's baseline too, keeping
     * the editor coherent — but an orthogonal-field difference never raises a
     * conflict.
     */
    async function resolveConflictAndRetry(
      kind: 'body' | 'title'
    ): Promise<'saved' | 'conflict' | 'failed'> {
      const { data: row, error: readError } = await supabase
        .from('documents')
        .select('title, body, last_event_at')
        .eq('id', documentId)
        .eq('org_id', activeOrgId)
        .maybeSingle();
      if (readError || !row) return 'failed';

      const serverTitle = row.title ?? '';
      const serverBody = row.body ?? '';

      if (kind === 'body') {
        if (serverBody !== baselineBodyRef.current) return 'conflict';
      } else {
        if (serverTitle !== baselineTitleRef.current) return 'conflict';
      }

      // Deciding field still matches baseline — only an orthogonal change
      // moved the timestamp. Refresh the expected timestamp and retry once.
      currentExpected = row.last_event_at;
      expectedTsRef.current = row.last_event_at;

      if (kind === 'body') {
        const { data, error } = await supabase.rpc('save_document', {
          p_document_id: documentId,
          p_body: bodyDraft,
          p_expected_updated_at: currentExpected
        });
        if (error) {
          if (isConflictError(error)) return 'conflict';
          onErrorRef.current?.(error.message ?? 'Save failed.');
          return 'failed';
        }
        const ret = (data ?? {}) as { updated_at?: string };
        if (ret.updated_at) {
          currentExpected = ret.updated_at;
          expectedTsRef.current = ret.updated_at;
        }
        baselineBodyRef.current = bodyDraft;
        // Reconcile the orthogonal field (title). If the title is NOT locally
        // edited, adopt the server's current title into both draft and
        // baseline so the editor shows the latest title and stays NOT dirty.
        // If the title IS locally edited, leave both untouched — the pending
        // title save later in onSave needs the ORIGINAL baseline so its
        // verify-then-decide can detect a genuine title conflict. Refreshing
        // the baseline here would mask a real conflict and silently revert a
        // concurrent rename.
        const titleLocallyEdited = titleDraft !== baselineTitleRef.current;
        if (!titleLocallyEdited) {
          setTitleDraft(serverTitle);
          baselineTitleRef.current = serverTitle;
        }
        return 'saved';
      }

      const { data, error } = await supabase.rpc('rename_document', {
        p_document_id: documentId,
        p_title: titleDraft,
        p_expected_updated_at: currentExpected
      });
      if (error) {
        if (isConflictError(error)) return 'conflict';
        onErrorRef.current?.(error.message ?? 'Save failed.');
        return 'failed';
      }
      const ret = (data ?? {}) as { updated_at?: string };
      if (ret.updated_at) {
        currentExpected = ret.updated_at;
        expectedTsRef.current = ret.updated_at;
      }
      baselineTitleRef.current = titleDraft;
      // Reconcile the orthogonal field (body). If the body is NOT locally
      // edited, adopt the server's current body into both draft and baseline
      // so the editor shows the latest body and stays NOT dirty. If the body
      // IS locally edited, leave both untouched — the pending body save needs
      // the ORIGINAL baseline so its verify-then-decide can detect a genuine
      // body conflict. Refreshing the baseline here would mask a real
      // conflict and silently revert a concurrent body edit.
      const bodyLocallyEdited = bodyDraft !== baselineBodyRef.current;
      if (!bodyLocallyEdited) {
        setBodyDraft(serverBody);
        baselineBodyRef.current = serverBody;
      }
      return 'saved';
    }

    // Mark the save in flight so a concurrent `reloadFromServer` defers and
    // does not stomp the baseline/timestamp refs we mutate below. Cleared in
    // the `finally` regardless of which exit path the save takes.
    saveInFlightRef.current = true;
    try {
    if (bodyDirty) {
      const { data, error } = await supabase.rpc('save_document', {
        p_document_id: documentId,
        p_body: bodyDraft,
        p_expected_updated_at: currentExpected
      });
      if (error) {
        if (isConflictError(error)) {
          const outcome = await resolveConflictAndRetry('body');
          if (outcome === 'conflict') {
            setSaveState('conflict');
            return;
          }
          if (outcome === 'failed') {
            setSaveState('failed');
            return;
          }
          // outcome === 'saved' — resync the working timestamp from the
          // durable ref, then fall through to the title save.
          currentExpected = expectedTsRef.current!;
        } else {
          setSaveState('failed');
          onErrorRef.current?.(error.message ?? 'Save failed.');
          return;
        }
      } else {
        const ret = (data ?? {}) as { updated_at?: string };
        if (ret.updated_at) {
          currentExpected = ret.updated_at;
          expectedTsRef.current = ret.updated_at;
        }
        baselineBodyRef.current = bodyDraft;
      }
    }

    if (titleDirty) {
      // Title goes through the rename_document RPC because the documents
      // table has no client UPDATE policy (writes are RPC-mediated by
      // design; see the documents_schema migration header).
      const { data, error } = await supabase.rpc('rename_document', {
        p_document_id: documentId,
        p_title: titleDraft,
        p_expected_updated_at: currentExpected
      });
      if (error) {
        if (isConflictError(error)) {
          const outcome = await resolveConflictAndRetry('title');
          if (outcome === 'conflict') {
            setSaveState('conflict');
            return;
          }
          if (outcome === 'failed') {
            setSaveState('failed');
            return;
          }
          // outcome === 'saved' — resync the working timestamp from the
          // durable ref, then fall through to the version-snapshot insert.
          currentExpected = expectedTsRef.current!;
        } else {
          setSaveState('failed');
          onErrorRef.current?.(error.message ?? 'Save failed.');
          return;
        }
      } else {
        const ret = (data ?? {}) as { updated_at?: string };
        if (ret.updated_at) {
          currentExpected = ret.updated_at;
          expectedTsRef.current = ret.updated_at;
        }
        baselineTitleRef.current = titleDraft;
      }
    }

    const { data: sess } = await supabase.auth.getSession();
    const uid = sess.session?.user.id;
    if (uid) {
      await supabase.from('version_snapshots').insert({
        subject_kind: 'document',
        subject_id: documentId,
        title: titleDraft,
        body: bodyDraft,
        snapshot_by: uid
      });
      setHistoryTick((n) => n + 1);
    }

    setSaveState('saved');
    } finally {
      saveInFlightRef.current = false;
    }
  }, [load.kind, documentId, dirty, bodyDirty, titleDirty, bodyDraft, titleDraft, activeOrgId]);

  const resetDrafts = useCallback(() => {
    setTitleDraft(baselineTitleRef.current);
    setBodyDraft(baselineBodyRef.current);
  }, []);

  const applyRestoredVersion = useCallback(
    (snapshot: { title: string; body: string; lastEventAt: string }) => {
      expectedTsRef.current = snapshot.lastEventAt;
      baselineTitleRef.current = snapshot.title;
      baselineBodyRef.current = snapshot.body;
      setTitleDraft(snapshot.title);
      setBodyDraft(snapshot.body);
      setSaveState('saved');
    },
    []
  );

  const mutateLoadedDoc = useCallback(
    (updater: (doc: DocumentEditorLoadedDoc) => DocumentEditorLoadedDoc) => {
      setLoad((prev) => (prev.kind === 'ready' ? { kind: 'ready', doc: updater(prev.doc) } : prev));
    },
    []
  );

  /**
   * Silent background refresh of server-confirmed state. Re-runs the same
   * `SELECT` the initial load runs, then resets the baseline/timestamp refs
   * and the in-flight `load` ready row from the fresh row. Used by the caller
   * (Task 3.2) when a committed agent write to the OPEN document arrives.
   *
   * Invariants:
   *  - `load.kind` MUST stay `'ready'` throughout — this is a background
   *    refresh, not a visible reload, so it never flips to `'loading'`.
   *  - It defers entirely when a user-initiated save is in flight
   *    (`saveInFlightRef`), so it cannot stomp the refs that save is mutating
   *    nor double-resolve it. The caller re-fires after the save settles if
   *    still needed (or the post-save baseline already reflects the write).
   *  - It is a no-op unless the document is already loaded (`kind === 'ready'`)
   *    with a known id, so it cannot resurrect a `notfound`/`error` view.
   */
  const reloadFromServer = useCallback(async () => {
    if (!documentId) return;
    if (saveInFlightRef.current) return;
    if (load.kind !== 'ready') return;
    if (!activeOrgId) return;

    const { data, error } = await supabase
      .from('documents')
      .select(
        'id, title, body, last_event_at, org_id, type_id'
      )
      .eq('id', documentId)
      .eq('org_id', activeOrgId)
      .maybeSingle();

    // A save may have started between the guard check above and the SELECT
    // resolving; if so, abandon this refresh so the save stays authoritative.
    if (saveInFlightRef.current) return;
    if (error || !data) return;

    const doc: DocumentEditorLoadedDoc = data as DocumentEditorLoadedDoc;
    // Snapshot the editor viewport (scroll + caret) BEFORE the draft swap so the
    // controlled-textarea value replacement below — which collapses the caret
    // and can shift the scrolling stage — is invisible to the user. Restored on
    // the next frame, after React commits the new value. This is the
    // doc-side half of the scroll-jump fix: an inline agent-write reconcile must
    // not move the user's view or caret.
    const snapshot = snapshotEditorViewport(getViewportAnchorRef.current?.());
    expectedTsRef.current = doc.last_event_at;
    baselineTitleRef.current = doc.title ?? '';
    baselineBodyRef.current = doc.body ?? '';
    setTitleDraft(doc.title ?? '');
    setBodyDraft(doc.body ?? '');
    // Mutate the in-flight ready row in place. `load.kind` never leaves
    // `'ready'`, so the editor does not flash a spinner or remount children.
    setLoad((prev) => (prev.kind === 'ready' ? { kind: 'ready', doc } : prev));
    snapshot.restore();
  }, [documentId, load.kind, activeOrgId]);

  const setType = useCallback(
    async (typeId: string): Promise<boolean> => {
      if (load.kind !== 'ready' || !documentId) return false;
      if (load.doc.type_id === typeId) return true;
      const { data, error } = await supabase.rpc('set_document_type', {
        p_document_id: documentId,
        p_type_id: typeId,
        p_expected_updated_at: expectedTsRef.current
      });
      if (error) {
        onErrorRef.current?.(error.message ?? 'Could not change type.');
        return false;
      }
      const ret = (data ?? {}) as {
        ok?: boolean;
        updated_at?: string;
        error?: string;
      };
      if (ret.ok === false) {
        onErrorRef.current?.(ret.error ?? 'Could not change type.');
        return false;
      }
      // Refresh the optimistic timestamp so a subsequent body save does not
      // send a stale p_expected_updated_at (set_document_type bumped it).
      if (ret.updated_at) expectedTsRef.current = ret.updated_at;
      mutateLoadedDoc((doc) => ({
        ...doc,
        type_id: typeId,
        last_event_at: ret.updated_at ?? doc.last_event_at
      }));
      return true;
    },
    [load, documentId, mutateLoadedDoc]
  );

  const bumpHistoryTick = useCallback(() => setHistoryTick((n) => n + 1), []);

  return {
    load,
    titleDraft,
    setTitleDraft,
    bodyDraft,
    setBodyDraft,
    saveState,
    dirty,
    titleDirty,
    bodyDirty,
    onSave,
    resetDrafts,
    applyRestoredVersion,
    mutateLoadedDoc,
    reloadFromServer,
    setType,
    historyTick,
    bumpHistoryTick
  };
}

function isConflictError(err: { code?: string; message?: string }): boolean {
  if (err.code === 'P0001') return true;
  return /conflict|expected/i.test(err.message ?? '');
}
